import type { PasswordResetFormData } from "@shared/schemas";
import { passwordResetSchema } from "@shared/schemas";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { usePasswordReset } from "@/api/mutations/register";
import { usePublicConfig } from "@/api/queries/config";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { usePageMeta } from "@/hooks/use-page-meta";
import { ApiError } from "@/lib/api-client";
import { zodResolver } from "@/lib/zod-resolver";

export const Route = createFileRoute("/_auth/forgot-password")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const passwordResetMutation = usePasswordReset();
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const { data: config } = usePublicConfig();

	usePageMeta({
		title: config ? `Passwort vergessen - ${config.seo.defaultTitle}` : undefined,
		noIndex: true,
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<PasswordResetFormData>({
		resolver: zodResolver(passwordResetSchema),
		defaultValues: {
			email: "",
		},
	});

	async function onSubmit(data: PasswordResetFormData) {
		setSuccessMessage(null);
		try {
			const result = await passwordResetMutation.mutateAsync(data);
			setSuccessMessage(result.message);
		} catch {
			// Fehler wird über passwordResetMutation.error angezeigt
		}
	}

	const errorMessage =
		passwordResetMutation.error instanceof ApiError
			? passwordResetMutation.error.message
			: passwordResetMutation.error?.message;

	const isSubmitted = !!successMessage;

	return (
		<div className="space-y-6">
			<div className="text-center">
				<h1 className="text-2xl font-bold text-text">Passwort vergessen</h1>
				<p className="mt-2 text-base text-text-secondary">
					Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
				</p>
			</div>

			<form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
				<Input
					{...register("email")}
					type="email"
					label="E-Mail"
					placeholder="deine@email.de"
					error={errors.email?.message}
					autoComplete="email"
					fullWidth
					disabled={isSubmitted}
				/>

				<Button
					type="submit"
					variant="primary"
					fullWidth
					loading={passwordResetMutation.isPending}
					disabled={isSubmitted}
				>
					Absenden
				</Button>

				{successMessage && (
					<output className="block text-success text-base text-center">{successMessage}</output>
				)}

				{errorMessage && !successMessage && (
					<p className="text-error text-base text-center" role="alert">
						{errorMessage}
					</p>
				)}
			</form>

			<div className="text-center">
				<Link to="/login" className="text-base text-primary hover:underline">
					Zurück zum Login
				</Link>
			</div>
		</div>
	);
}
